import React, { useEffect, useRef, useState } from 'react';
import CarouselSection from './CarouselSection';
import { DEFAULT_SECTIONS, SECTION_TYPES, sectionEndpoint, sectionNote, browseHref } from '../config/sectionTypes';

// One carousel that only fetches its data once it scrolls near the viewport.
// Previously every carousel fetched on mount, so the landing page fired ~6
// parallel round-trips to us-east-1 up front — slow for distant users. Now the
// below-the-fold carousels load lazily (the loading skeleton reserves height, so
// the placeholders stack down the page and only the visible ones intersect).
function LazyCarousel({ apiBase, section, token }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState({ items: [], loading: true });
  const def = SECTION_TYPES[section.type];
  const authed = def && def.authed;

  // Reveal when the placeholder scrolls within 400px of the viewport.
  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    // No IntersectionObserver (old browsers / tests): load immediately.
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const obs = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setVisible(true); obs.disconnect(); } },
      { rootMargin: '400px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  // Fetch once, when first revealed.
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const url = sectionEndpoint(section);
    if (!url) { setState({ items: [], loading: false }); return undefined; }
    if (authed && !token) { setState({ items: [], loading: false }); return undefined; }
    const options = authed ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    setState({ items: [], loading: true });
    fetch(`${apiBase}${url}`, options)
      .then((res) => (res.ok ? res.json() : { results: [] }))
      .then((json) => { if (!cancelled) setState({ items: json.results || [], loading: false }); })
      .catch(() => { if (!cancelled) setState({ items: [], loading: false }); });
    return () => { cancelled = true; };
  }, [visible, apiBase, section, token, authed]);

  const viewAllHref = authed ? null : browseHref(section);
  return (
    <div ref={ref}>
      <CarouselSection
        title={section.title}
        note={sectionNote(section)}
        items={state.items}
        isLoading={state.loading}
        viewAllHref={viewAllHref}
      />
    </div>
  );
}

// Renders an ordered list of carousel sections. Defaults to the standard landing
// carousels; later phases pass a user's saved layout via the `sections` prop.
// `token` is used for section types that require auth (e.g. "For You").
export default function DashboardCarousels({ apiBase, sections = DEFAULT_SECTIONS, token }) {
  return (
    <>
      {sections.map((section) => {
        if (section.visible === false) return null;
        // Skip section types that aren't fetchable yet (e.g. genre/discover
        // wired in a later phase) so they don't render an endless skeleton.
        if (!sectionEndpoint(section)) return null;
        return <LazyCarousel key={section.id} apiBase={apiBase} section={section} token={token} />;
      })}
    </>
  );
}
